import { UserButton } from '@/components'
import { OrganizationSwitcher } from '@/components'
import { isBlocked, isOpenWithoutSubscription, organisationGuard, subscriptionGuard } from '@/guards'
import { EntitlementsProvider } from '@/lib'
import { useAuth } from '@/lib'
import { CommonUiProvider, logger, type MainNavConfig, OrgStoreProvider, type SettingsNavConfig } from '@/ui'
import { GlobalChat } from '@/ui/global-chat/GlobalChat'
import { useEntitlementIndex } from '@jobilla/nexus-sdk'
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk'
import { createFileRoute, Link, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'

export const Route = createFileRoute('/_auth/company/$companyId')({
	beforeLoad: async ({ params, location, context }) => {
		const { jobillaOrgId, workosOrgId, isDemo } = await organisationGuard(context.queryClient, params)
		await subscriptionGuard(context.queryClient, {
			jobillaOrgId,
			companyId: params.companyId,
			pathname: location.pathname,
		})
		return { jobillaOrgId, workosOrgId, isDemo }
	},
	component: RouteComponent,
})

function RouteComponent() {
	const { companyId } = Route.useParams()
	const { jobillaOrgId, workosOrgId, isDemo } = Route.useRouteContext()
	const { switchOrganisation } = useAuth()
	const navigate = useNavigate()
	// The resolved location: while a navigation is pending this layout still renders the old
	// organisation, and redirecting on the target path would cancel an organisation switch.
	const pathname = useRouterState({ select: (state) => (state.resolvedLocation ?? state.location).pathname })
	const { data: entitlements } = useEntitlementIndex(jobillaOrgId, {
		enabled: !!jobillaOrgId,
		// Keep in sync with EntitlementsProvider, which observes the same query.
		staleTime: 30_000,
	})
	// The guard only runs on navigation; this catches access lapsing while the user stays on a page.
	const mustSubscribe = isBlocked(entitlements?.subscriptionAccess)
		&& !isOpenWithoutSubscription(pathname, companyId)
	const globalAiChatEnabled = useBooleanFlagValue('global-ai-chat', false)
	const showGlobalChat = globalAiChatEnabled && !isBlocked(entitlements?.subscriptionAccess)

	useEffect(() => {
		logger.setDemoOrganisation(isDemo)

		if (jobillaOrgId) {
			logger.setGroup('company', jobillaOrgId)
		}

		// Flags are evaluated for the organisation in the access token, so refetch them
		// only once the switch has put this organisation there.
		const refetchFlags = () => {
			void OpenFeature.setContext({ ...OpenFeature.getContext(), organisation: jobillaOrgId })
		}

		if (!workosOrgId) {
			refetchFlags()
			return
		}

		// A switch that settles after the user moved on to another organisation must not act.
		let isCurrent = true

		switchOrganisation(workosOrgId).then(
			() => {
				if (isCurrent) {
					refetchFlags()
				}
			},
			(error: unknown) => {
				if (!isCurrent) {
					return
				}

				logger.warn('Organisation switch failed', {
					category: 'auth',
					data: { error: error instanceof Error ? error.message : String(error) },
				})
				void navigate({ to: '/company', search: { openFailed: companyId }, replace: true })
			},
		)

		return () => {
			isCurrent = false
		}
	}, [jobillaOrgId, workosOrgId, isDemo, switchOrganisation, navigate, companyId])

	const mainNavConfig = useMemo<MainNavConfig>(
		() => ({
			logoLinker: (props) => <Link to="/company/$companyId" params={{ companyId }} {...props} />,
			campaignsLinker: (props) => <Link to="/company/$companyId" params={{ companyId }} {...props} />,
			talentPoolLinker: (props) => (
				<Link
					to="/company/$companyId/talent-pool"
					params={{ companyId }}
					{...props}
				/>
			),
			analyticsLinker: (props) => (
				<Link
					to="/company/$companyId/analytics"
					params={{ companyId }}
					{...props}
				/>
			),
			whatsAppLinker: (props) => (
				<Link
					to="/company/$companyId/whatsapp"
					params={{ companyId }}
					{...props}
				/>
			),
			newCampaignLinker: (props) => (
				<Link
					to="/company/$companyId/campaign/new"
					params={{ companyId }}
					onClick={() => logger.track('Clicked "New Campaign"')}
					{...props}
				/>
			),
			profileLinker: () => (
				<motion.div layoutId="profile-button" className="flex items-center justify-center">
					<UserButton />
				</motion.div>
			),
			preferencesLinker: (props) => (
				<Link
					to="/company/$companyId/settings"
					params={{ companyId }}
					{...props}
				/>
			),
		}),
		[companyId],
	)

	const settingsNavConfig = useMemo<SettingsNavConfig>(
		() => ({
			organizationLinker: (props) => (
				<Link
					to="/company/$companyId/settings/organization"
					params={{ companyId }}
					{...props}
				/>
			),
			membersLinker: (props) => (
				<Link
					to="/company/$companyId/settings/members"
					params={{ companyId }}
					{...props}
				/>
			),
			privacyLinker: (props) => (
				<Link
					to="/company/$companyId/settings/privacy"
					params={{ companyId }}
					{...props}
				/>
			),
			campaignSettingsLinker: (props) => (
				<Link
					to="/company/$companyId/settings/campaign-settings"
					params={{ companyId }}
					{...props}
				/>
			),
			candidateCenterLinker: (props) => (
				<Link
					to="/company/$companyId/settings/candidate-center"
					params={{ companyId }}
					{...props}
				/>
			),
			customFieldLinker: (props) => (
				<Link
					to="/company/$companyId/settings/custom-fields"
					params={{ companyId }}
					{...props}
				/>
			),
			exportLinker: (props) => (
				<Link
					to="/company/$companyId/settings/export"
					params={{ companyId }}
					{...props}
				/>
			),
			brandingLibraryLinker: (props) => (
				<Link
					to="/company/$companyId/settings/branding-library"
					params={{ companyId }}
					{...props}
				/>
			),
			assetHubLinker: (props) => (
				<Link
					to="/company/$companyId/settings/asset-hub"
					params={{ companyId }}
					{...props}
				/>
			),
			inventoryLinker: (props) => (
				<Link
					to="/company/$companyId/settings/inventory"
					params={{ companyId }}
					{...props}
				/>
			),
			invoicesLinker: (props) => (
				<Link
					to="/company/$companyId/settings/invoices"
					params={{ companyId }}
					{...props}
				/>
			),
			walletLinker: (props) => (
				<Link
					to="/company/$companyId/settings/wallet"
					params={{ companyId }}
					{...props}
				/>
			),
			billingLinker: (props) => (
				<Link
					to="/company/$companyId/settings/billing"
					params={{ companyId }}
					{...props}
				/>
			),
			whatsappLinker: (props) => (
				<Link
					to="/company/$companyId/settings/whatsapp"
					params={{ companyId }}
					{...props}
				/>
			),
			emailTemplatesLinker: (props) => (
				<Link
					to="/company/$companyId/settings/email-templates"
					params={{ companyId }}
					{...props}
				/>
			),
			fontsLinker: (props) => (
				<Link
					to="/company/$companyId/settings/fonts"
					params={{ companyId }}
					{...props}
				/>
			),
			designSimulatorLinker: (props) => (
				<Link
					to="/company/$companyId/settings/design-simulator"
					params={{ companyId }}
					{...props}
				/>
			),
			adDesignsLinker: (props) => (
				<Link
					to="/company/$companyId/settings/ad-designs"
					params={{ companyId }}
					{...props}
				/>
			),
			externalConnectionsLinker: (props) => (
				<Link
					to="/company/$companyId/settings/external-connections"
					params={{ companyId }}
					{...props}
				/>
			),
			careerSiteLinker: (props) => (
				<Link
					to="/company/$companyId/settings/career-site"
					params={{ companyId }}
					{...props}
				/>
			),
			aiSettingsLinker: (props) => (
				<Link
					to="/company/$companyId/settings/ai"
					params={{ companyId }}
					{...props}
				/>
			),
		}),
		[companyId],
	)

	const orgSwitcher = useMemo(() => <OrganizationSwitcher currentSlug={companyId} />, [companyId])

	if (mustSubscribe) {
		return <Navigate to="/company/$companyId/subscribe" params={{ companyId }} replace={true} />
	}

	return (
		<OrgStoreProvider orgId={jobillaOrgId || companyId}>
			<EntitlementsProvider>
				<CommonUiProvider
					orgSwitcher={orgSwitcher}
					mainNav={mainNavConfig}
					settingsNav={settingsNavConfig}
				>
					<Outlet />
				</CommonUiProvider>
				{showGlobalChat ? createPortal(<GlobalChat />, document.body) : null}
			</EntitlementsProvider>
		</OrgStoreProvider>
	)
}
